import React, { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import styles from './ForgotPassword.module.scss';
import logo from '../../../assets/images/Color_CombatTv_Logo.svg';
import { toast } from 'react-toastify';
import { useUserService } from "../../../services/user.service";
import { Routes } from '../../../types/constants';

const ForgotPassword = () => {
    const { forgotPassword } = useUserService();
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const submitForm = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        setFormError(null);
        setSuccessMessage(null);

        const normalizedEmail = email.trim();
        if (!normalizedEmail) {
            setFormError('Email is required.');
            return;
        }

        try {
            setIsSubmitting(true);
            const response = await forgotPassword(normalizedEmail) as { errors?: string; Errors?: string[] };
            const requestError = response?.errors || response?.Errors?.[0];
            if (requestError) {
                throw new Error(requestError);
            }

            toast.success('Reset link sent to your email');
            setSuccessMessage('If your email exists in Combat TV, a reset link is on the way.');
        } catch (error) {
            toast.error('Failed to send email');
            const errorMessage = error instanceof Error ? error.message : '';
            setFormError(errorMessage || 'Failed to send reset email. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <main className={styles.page}>
            <section className={styles.hero}>
                <div className={styles.heroContent}>
                    <span className={styles.heroTag}>Account recovery</span>
                    <h1 className={styles.heroTitle}>Forgot your password?</h1>
                    <p className={styles.heroSubtitle}>
                        No problem. Enter the email linked to your account and we will send a secure reset link.
                    </p>
                </div>
                <div className={styles.heroVisual}>
                    <img src={logo} alt="Combat TV logo" className={styles.logo} />
                </div>
            </section>

            <section className={styles.section}>
                <div className={styles.card}>
                    <div className={styles.cardHeader}>
                        <h2>Request password reset</h2>
                        <p>We will email instructions so you can set a new password.</p>
                    </div>

                    {formError ? <div className={styles.formError}>{formError}</div> : null}
                    {successMessage ? <div className={styles.formSuccess}>{successMessage}</div> : null}

                    <form id="forgotPasswordForm" className={styles.form} onSubmit={submitForm}>
                        <div className={styles.fieldGroup}>
                            <label htmlFor="forgot-password-email">Email address</label>
                            <input
                                id="forgot-password-email"
                                type="email"
                                placeholder="Enter your email"
                                value={email}
                                onChange={(event) => {
                                    setEmail(event.target.value);
                                    setFormError(null);
                                    setSuccessMessage(null);
                                }}
                                autoComplete="email"
                                required
                            />
                        </div>

                        <div className={styles.actions}>
                            <button
                                type="submit"
                                className={styles.primaryButton}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Sending...' : 'Send reset link'}
                            </button>
                            <Link to={Routes.LOGIN} className={styles.secondaryButton}>
                                Back to sign in
                            </Link>
                        </div>
                    </form>
                </div>
            </section>
        </main>
    );
};

export default ForgotPassword;
